import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Palette, MonitorSmartphone, Target, ChevronDown } from "lucide-react";
import { handleAnchor } from "../lib/site";
import { LogoSquare } from "./JLMark";
import { Particles } from "./Particles";
import { MaskLines, EASE } from "./Reveal";

const INDICATORS = [
  { icon: Palette, label: "Design personalizado" },
  { icon: MonitorSmartphone, label: "Experiência para celular e computador" },
  { icon: Target, label: "Foco em resultados e credibilidade" },
];

const LINES = [
  <>Sua empresa merece</>,
  <>um <span className="text-grad">site</span> à altura</>,
  <>do seu <span className="text-grad">potencial.</span></>,
];

const fade = (d) => ({ initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.9, delay: d, ease: EASE } });

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const fadeOut = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  const bgX = useTransform(sx, (v) => v * -14);
  const bgMY = useTransform(sy, (v) => v * -10);
  const logoX = useTransform(sx, (v) => v * 26);
  const logoY = useTransform(sy, (v) => v * 18);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="inicio" ref={ref} onMouseMove={onMove} data-testid="hero-section" className="grain relative isolate flex min-h-[100svh] items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-x-0 -top-[4%] -z-10 h-[108%]">
        <motion.div style={{ x: bgX, y: bgMY }} className="absolute -inset-6">
          <motion.img
            src={`${process.env.PUBLIC_URL}/img/hero-clean.webp`}
            alt="Mesa de trabalho escura com notebook iluminado em azul, ambiente da JL Nexus"
            className="kenburns h-full w-full object-cover object-[22%_center] lg:object-center"
            initial={{ opacity: 0, filter: "blur(12px) brightness(0.4)" }}
            animate={{ opacity: 1, filter: "blur(0px) brightness(1)" }}
            transition={{ duration: 1.8, ease: EASE }}
          />
        </motion.div>
      </motion.div>

      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#080B12]/95 via-[#080B12]/60 to-[#080B12]/10" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#080B12] via-transparent to-[#080B12]/50" />
      <div className="grid-lines absolute inset-0 -z-10" />
      <div className="scanlines absolute inset-0 -z-10 opacity-60" />
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="light-sweep absolute -top-1/4 left-0 h-[150%] w-1/4 bg-gradient-to-r from-transparent via-[#00C8FF]/10 to-transparent" />
      </div>
      <div className="pulse-glow absolute bottom-[8%] right-[18%] -z-10 h-40 w-[36rem] rounded-full bg-[#087BFF]/25 blur-[90px]" />
      <div className="pulse-glow absolute left-[-10%] top-[20%] -z-10 h-72 w-72 rounded-full bg-[#5A4BFF]/15 blur-[100px]" style={{ animationDelay: "2s" }} />
      <div className="absolute inset-0 -z-10"><Particles /></div>

      <motion.div style={{ y: contentY, opacity: fadeOut }} className="mx-auto grid w-full max-w-[1400px] grid-cols-1 items-center gap-10 px-5 pb-24 pt-32 sm:px-8 lg:grid-cols-12 lg:pt-28">
        <div className="lg:col-span-7">
          <motion.p {...fade(0.2)} data-testid="hero-eyebrow" className="font-mono-jl mb-7 inline-flex items-center gap-3 rounded-full border border-[#00C8FF]/25 bg-[#00C8FF]/5 px-4 py-2 text-[11px] uppercase tracking-[0.28em] text-[#7FE3FF] backdrop-blur-md sm:text-xs">
            <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00C8FF] opacity-70" /><span className="relative inline-flex h-2 w-2 rounded-full bg-[#00C8FF]" /></span>
            Desenvolvimento digital • JL Nexus
          </motion.p>
          <h1 data-testid="hero-title" className="font-display text-[2.15rem] font-semibold leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl xl:text-[4.6rem]">
            <MaskLines lines={LINES} delay={0.35} />
          </h1>
          <motion.p {...fade(0.9)} data-testid="hero-subtitle" className="mt-7 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            Criamos sites profissionais, modernos e estratégicos para apresentar seu negócio ao mundo, fortalecer sua marca e transformar visitantes em oportunidades reais.
          </motion.p>
          <motion.div {...fade(1.05)} className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a href="#contato" onClick={(e) => handleAnchor(e, "contato")} data-testid="hero-cta-primary" className="btn-shine group inline-flex h-14 items-center justify-center gap-3 rounded-full bg-[#087BFF] px-8 font-semibold text-white shadow-[0_0_40px_rgba(8,123,255,0.55)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_0_60px_rgba(0,200,255,0.65)]">
              Solicitar meu site
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a href="#portfolio" onClick={(e) => handleAnchor(e, "portfolio")} data-testid="hero-cta-secondary" className="inline-flex h-14 items-center justify-center gap-2 rounded-full border border-white/20 bg-white/5 px-8 font-medium text-white backdrop-blur-md transition-[background-color,border-color,transform] duration-300 hover:-translate-y-1 hover:border-[#00C8FF]/60 hover:bg-white/10">
              Conhecer nossos projetos
            </a>
          </motion.div>
          <motion.ul {...fade(1.2)} data-testid="hero-indicators" className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-x-7">
            {INDICATORS.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-2.5 text-sm text-slate-300">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#087BFF]/30 bg-[#087BFF]/10"><Icon className="h-4 w-4 text-[#00C8FF]" /></span>
                {label}
              </li>
            ))}
          </motion.ul>
        </div>

        <motion.div
          style={{ x: logoX, y: logoY }}
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.6, delay: 0.5, ease: EASE }}
          className="relative flex items-center justify-center lg:col-span-5"
          data-testid="jl-logo-3d"
        >
          <div className="float-y relative flex items-center justify-center" style={{ perspective: 900 }}>
            <div className="pulse-glow absolute inset-[12%] rounded-full bg-[#087BFF]/40 blur-[70px]" />
            <motion.img
              src={`${process.env.PUBLIC_URL}/img/logo-square.webp`}
              alt="Logotipo oficial JL Nexus Soluções Digitais"
              className="relative w-[290px] max-w-full sm:w-[400px]"
              animate={{ rotateY: [-14, 14, -14], rotateX: [6, -4, 6] }}
              transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
        </motion.div>
      </motion.div>

      <a href="#servicos" onClick={(e) => handleAnchor(e, "servicos")} data-testid="hero-scroll-indicator" aria-label="Rolar para serviços" className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-slate-400 transition-colors hover:text-white sm:flex">
        Role
        <ChevronDown className="h-4 w-4 animate-bounce" />
      </a>
    </section>
  );
};
